import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Empty, Skeleton } from "antd";
import type { Texture } from "@textureninja/shared";
import { useCategoryTags, useTextureByKey, useTextures } from "@/lib/queries.js";
import { categoryMap, type CategoryNode } from "@/data/category-tree.js";
import { Gallery } from "./Gallery.js";
import { FilterByTag } from "./FilterByTag.js";
import { SubcategorySelection } from "./SubcategorySelection.js";
import { TextureModal } from "./TextureModal.js";

interface RouteParams {
  category?: string;
  id?: string;
  subcategories?: string;
}

function resolveCategories(params: RouteParams): {
  categoryIds: number[];
  childCategories: CategoryNode[];
  parent: CategoryNode | null;
} {
  const { id, category, subcategories } = params;
  if (!id) return { categoryIds: [], childCategories: [], parent: null };

  const parent = categoryMap.get(Number(id));
  if (!parent) return { categoryIds: [Number(id)], childCategories: [], parent: null };

  if (category && parent.children) {
    let ids = [parent.id, ...parent.children.map(([, c]) => c.id)];
    if (subcategories) {
      ids = subcategories
        .split(",")
        .map((s) => parseInt(s, 10))
        .filter((n) => !isNaN(n));
    }
    return {
      categoryIds: ids,
      childCategories: [parent, ...parent.children.map(([, c]) => c)],
      parent,
    };
  }
  return { categoryIds: [parent.id], childCategories: [], parent };
}

export function TextureBrowser() {
  const params = useParams() as RouteParams;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tags = searchParams.getAll("tags");
  const searchTag = searchParams.get("tag");
  const textureKeyInUrl = searchParams.get("texture");

  const { categoryIds, childCategories, parent } = useMemo(
    () => resolveCategories(params),
    [params],
  );

  const textures = useTextures(
    {
      ...(categoryIds.length > 0 ? { category_id: categoryIds } : {}),
      ...(tags.length > 0 ? { tags } : {}),
      ...(searchTag ? { tag: searchTag } : {}),
    },
    categoryIds.length > 0 || tags.length > 0 || !!searchTag,
  );
  const categoryTags = useCategoryTags(categoryIds.length > 0 ? categoryIds : null);
  const modalTexture = useTextureByKey(textureKeyInUrl);

  const [openModal, setOpenModal] = useState<Texture | null>(null);
  const activeModal = openModal ?? modalTexture.data ?? null;

  function handleOpen(t: Texture) {
    setOpenModal(t);
    const next = new URLSearchParams(searchParams);
    next.set("texture", t.key);
    navigate({ search: `?${next.toString()}` }, { replace: false });
  }

  function handleClose() {
    setOpenModal(null);
    const next = new URLSearchParams(searchParams);
    next.delete("texture");
    navigate({ search: next.toString() ? `?${next.toString()}` : "" });
  }

  function toggleSubcategory(id: number) {
    const { category, id: parentId, subcategories } = params;
    if (!category || !parentId) return;
    const current = subcategories ? subcategories.split(",") : [];
    const idx = current.indexOf(String(id));
    if (idx > -1) current.splice(idx, 1);
    else current.push(String(id));
    navigate(`/category/${category}/${parentId}/${current.join(",")}`);
  }

  const title = searchTag ? `#${searchTag}` : (parent?.category ?? "Search");
  const totalCount = textures.data?.length ?? 0;

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          padding: "0 0 12px",
          marginBottom: 8,
          borderBottom: "1px solid var(--tn-border-faint)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="tn-eyebrow">
            {searchTag ? "Tag" : parent?.children ? "Collection" : "Category"}
          </span>
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              textTransform: "capitalize",
            }}
          >
            {title}
          </h1>
        </div>
        {textures.data && (
          <span
            style={{
              fontSize: 12,
              color: "rgba(255,255,255,0.5)",
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "0.02em",
            }}
          >
            {totalCount.toLocaleString()} {totalCount === 1 ? "texture" : "textures"}
          </span>
        )}
      </div>

      {childCategories.length > 0 && (
        <SubcategorySelection
          categories={childCategories}
          selection={params.subcategories ?? ""}
          onToggle={toggleSubcategory}
        />
      )}
      {categoryTags.data && <FilterByTag tags={categoryTags.data} />}

      {textures.isLoading && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 8,
            marginTop: 16,
          }}
        >
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton.Image key={i} active style={{ width: "100%", height: 200 }} />
          ))}
        </div>
      )}

      {textures.data && textures.data.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Gallery textures={textures.data} onOpen={handleOpen} />
        </div>
      )}

      {textures.data && textures.data.length === 0 && (
        <Empty
          description={
            <span style={{ color: "rgba(255,255,255,0.55)" }}>
              {searchTag
                ? `No textures found for "${searchTag}"`
                : "No textures match the selected filters"}
            </span>
          }
          style={{ padding: "64px 16px" }}
        />
      )}

      {activeModal && <TextureModal texture={activeModal} open={true} onClose={handleClose} />}
    </div>
  );
}
